import { useMutation, useQuery } from '@tanstack/react-query';
import { administrationClient, unwrap, createResource } from '@/shared/api-client';
import { useInvalidateRoleAssignments } from '@/modules/users/api';

export interface Role {
  id: string;
  name: string;
  isSystem: boolean;
  permissions: string[];
  createdAt: string;
  updatedAt: string;
}

export interface RoleListResponse {
  items: Role[];
}

export interface CreateRoleInput {
  name: string;
  permissions: string[];
}

export interface UpdateRoleInput {
  name?: string;
  permissions?: string[];
  confirmUnresolvedVenueAccess?: boolean;
}

/**
 * The `GET /api/v1/franchise-administration/permissions` catalogue, grouped area-first then module then action
 * (ADR 0032). `area` is the PermissionArea wire value (`franchise_administration`
 * or `venue_workspace`).
 */
export interface PermissionCatalogue {
  areas: {
    area: string;
    modules: {
      module: string;
      actions: { action: string; permissions: { code: string }[] }[];
    }[];
  }[];
}

const listRoles = (): Promise<RoleListResponse> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/roles'),
  ) as Promise<RoleListResponse>;

const getRole = (id: string): Promise<Role> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/roles/{id}', {
      params: { path: { id } },
    }),
  ) as Promise<Role>;

export const createRole = (input: CreateRoleInput): Promise<Role> =>
  unwrap(
    administrationClient.POST('/api/v1/franchise-administration/roles', { body: input }),
  ) as Promise<Role>;

export const updateRole = (id: string, input: UpdateRoleInput): Promise<Role> =>
  unwrap(
    administrationClient.PATCH('/api/v1/franchise-administration/roles/{id}', {
      params: { path: { id } },
      body: input,
    }),
  ) as Promise<Role>;

export const deleteRole = (id: string): Promise<void> =>
  unwrap(
    administrationClient.DELETE('/api/v1/franchise-administration/roles/{id}', {
      params: { path: { id } },
    }),
  );

const getPermissionCatalogue = (): Promise<PermissionCatalogue> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/permissions'),
  ) as Promise<PermissionCatalogue>;

/** Roles routed through the shared resource factory (ADR 0012); the typed fetch
 *  closures above stay per-module, the react-query ribbon is the factory's. Update and
 *  delete stay bespoke below because they also invalidate role assignments. */
const roles = createResource({
  key: ['roles'],
  list: listRoles,
  get: getRole,
  create: createRole,
});

export const useRoles = roles.useList;
export const useCreateRole = roles.useCreate;

/** Updating or deleting a Role also refreshes the `['role-assignments']` prefix: a permission
 *  change can make assignments need venues (or stop needing them), and a delete drops the
 *  Role's assignments, so the Users page's "Needs venue access" summary and per-user panels
 *  would otherwise stay stale until their staleTime lapses. */
function useInvalidateRolesAndAssignments() {
  const invalidateRoles = roles.useInvalidate();
  const invalidateAssignments = useInvalidateRoleAssignments();
  return () => Promise.all([invalidateRoles(), invalidateAssignments()]);
}

export function useUpdateRole() {
  const invalidate = useInvalidateRolesAndAssignments();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateRoleInput }) => updateRole(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteRole() {
  const invalidate = useInvalidateRolesAndAssignments();
  return useMutation({ mutationFn: deleteRole, onSuccess: invalidate });
}

export function usePermissionCatalogue() {
  return useQuery({ queryKey: ['permissions'], queryFn: getPermissionCatalogue });
}
